import { extends as _extends } from '../_virtual/_rollupPluginBabelHelpers.js';
import { isNumber } from '@sajari/react-sdk-utils';
import React__default, { useCallback, useEffect, useRef } from 'react';
import '../ContextProvider/controllers/Listener.esm.js';
import '../ContextProvider/controllers/analytics/Analytics.esm.js';
import '../ContextProvider/controllers/analytics/GoogleAnalytics.esm.js';
import FilterBuilder from '../ContextProvider/controllers/filters/FilterBuilder.esm.js';
import '../ContextProvider/controllers/filters/RangeFilterBuilder.esm.js';
import '../ContextProvider/controllers/filters/combineFilters.esm.js';
import '../ContextProvider/controllers/Response.esm.js';
import '../ContextProvider/controllers/tracking/Tracking.esm.js';
import '../ContextProvider/controllers/tracking/ClickTracking.esm.js';
import '../ContextProvider/controllers/tracking/EventTracking.esm.js';
import '../ContextProvider/controllers/tracking/NoTracking.esm.js';
import '../ContextProvider/controllers/tracking/PosNegTracking.esm.js';
import '../ContextProvider/controllers/Pipeline.esm.js';
import '../ContextProvider/controllers/Variables.esm.js';
import useFilter from '../useFilter/index.esm.js';
import usePagination from '../usePagination/index.esm.js';
import useQuery from '../useQuery/index.esm.js';
import useQueryParam from '../useQueryParam/index.esm.js';
import useRangeFilter from '../useRangeFilter/index.esm.js';
import useResultsPerPage from '../useResultsPerPage/index.esm.js';
import useSearchContext from '../useSearchContext/index.esm.js';
import useSorting from '../useSorting/index.esm.js';
import { rangeToParam, paramToRange, isRange } from '../utils/queryParams.esm.js';
import { defaultURLParamKeys } from './config.esm.js';

var FilterWatcher = function FilterWatcher(_ref) {
  var filter = _ref.filter,
      replace = _ref.replace,
      delay = _ref.delay;
  var key = filter.getField() || filter.getName();
  var name = filter.getName();

  var _useFilter = useFilter(name),
      setSelected = _useFilter.setSelected,
      selected = _useFilter.selected;

  var setFilterParam = useQueryParam(key, {
    debounce: delay,
    replace: replace,
    callback: replace ? undefined : function (value) {
      setSelected(value === '' ? [] : value.split(','));
    }
  });
  useEffect(function () {
    setFilterParam(selected);
  }, [selected]);
  return null;
};

var RangeFilterWatcher = function RangeFilterWatcher(_ref2) {
  var filter = _ref2.filter,
      replace = _ref2.replace,
      delay = _ref2.delay;
  var key = filter.getField() || filter.getName();
  var name = filter.getName();

  var _useRangeFilter = useRangeFilter(name),
      range = _useRangeFilter.range,
      setRange = _useRangeFilter.setRange,
      min = _useRangeFilter.min,
      max = _useRangeFilter.max,
      reset = _useRangeFilter.reset;

  var allowSetParam = useRef(false);

  var _useSearchContext = useSearchContext(),
      response = _useSearchContext.response,
      results = _useSearchContext.results;

  var setFilterParam = useQueryParam(key, {
    debounce: delay,
    replace: replace,
    callback: replace ? undefined : function (value) {
      var rangeValue = paramToRange(value);

      if (!isRange(rangeValue) || rangeValue[0] === rangeValue[1]) {
        if (filter.isAggregate()) {
          reset();
        } else {
          setRange([min, max]);
        }
      } else {
        setRange(rangeValue);
      }
    }
  });
  var setMinMaxParam = useQueryParam(key + "_min_max", {
    debounce: delay,
    replace: true
  });
  useEffect(function () {
    if (allowSetParam.current) {
      if (range) {
        var shouldSetNewValue = range[0] !== min || range[1] !== max;
        setFilterParam(shouldSetNewValue ? rangeToParam(range) : '');

        if (filter.isAggregate()) {
          setMinMaxParam(shouldSetNewValue ? filter.getMinMax().join(':') : '');
        }
      } else {
        setMinMaxParam('');
        setFilterParam('');
      }
    }
  }, [range]);
  useEffect(function () {
    if (response) {
      allowSetParam.current = true;
    }
  }, [response]);
  useEffect(function () {
    if (results && filter.getFrozen()) {
      setTimeout(function () {
        filter.setFrozen(false);
      });
    }
  }, [results]);
  return null;
};

var ParamWatcher = function ParamWatcher(_ref3) {
  var delay = _ref3.delay,
      replace = _ref3.replace,
      queryParam = _ref3.queryParam;
  var key = queryParam.key,
      callback = queryParam.callback,
      defaultValue = queryParam.defaultValue,
      value = queryParam.value;
  var setParam = useQueryParam(key, {
    debounce: delay,
    replace: replace,
    defaultValue: defaultValue,
    callback: replace ? undefined : callback
  });
  useEffect(function () {
    setParam(value);
  }, [value]);
  return null;
};

var URLStateSync = function URLStateSync(props) {
  if (props === void 0) {
    props = {};
  }

  var _props = props,
      _props$delay = _props.delay,
      delay = _props$delay === void 0 ? 500 : _props$delay,
      _props$replace = _props.replace,
      replace = _props$replace === void 0 ? false : _props$replace,
      _props$extendedParams = _props.extendedParams,
      extendedParams = _props$extendedParams === void 0 ? [] : _props$extendedParams,
      defaultParamKeysProps = _props.paramKeys;

  var defaultParamKeys = _extends({}, defaultURLParamKeys, defaultParamKeysProps);

  var _useSearchContext2 = useSearchContext(),
      _useSearchContext2$fi = _useSearchContext2.filters,
      filterBuilders = _useSearchContext2$fi === void 0 ? [] : _useSearchContext2$fi;

  var _useQuery = useQuery(),
      query = _useQuery.query,
      setQuery = _useQuery.setQuery;

  var _useSorting = useSorting(),
      sorting = _useSorting.sorting,
      setSorting = _useSorting.setSorting;

  var _usePagination = usePagination(),
      page = _usePagination.page,
      setPageInner = _usePagination.setPage;

  var setPage = useCallback(function (numStr) {
    var num = Number(numStr);

    if (isNumber(num)) {
      setPageInner(num);
    }
  }, [setPageInner]);

  var _useResultsPerPage = useResultsPerPage(),
      resultsPerPage = _useResultsPerPage.resultsPerPage,
      setResultsPerPage = _useResultsPerPage.setResultsPerPage,
      defaultResultsPerPage = _useResultsPerPage.defaultResultsPerPage;

  var paramWatchers = [{
    key: defaultParamKeys.q,
    value: query,
    callback: setQuery
  }, {
    key: defaultParamKeys.sort,
    value: sorting,
    callback: setSorting
  }, {
    key: defaultParamKeys.resultsPerPage,
    value: resultsPerPage,
    defaultValue: defaultResultsPerPage,
    callback: function callback(value) {
      setResultsPerPage(Number(value) || defaultResultsPerPage);
    }
  }, {
    key: defaultParamKeys.page,
    defaultValue: -1,
    value: page === 1 ? -1 : page,
    callback: setPage
  }].concat(extendedParams.filter(function (_ref4) {
    var key = _ref4.key;
    return ![defaultParamKeys.q, defaultParamKeys.sort, defaultParamKeys.resultsPerPage].includes(key);
  }));
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, filterBuilders.map(function (filter) {
    return filter instanceof FilterBuilder ? /*#__PURE__*/React__default.createElement(FilterWatcher, _extends({
      key: filter.getField()
    }, {
      replace: replace,
      delay: delay,
      filter: filter
    })) : /*#__PURE__*/React__default.createElement(RangeFilterWatcher, _extends({
      key: filter.getField()
    }, {
      replace: replace,
      delay: delay,
      filter: filter
    }));
  }), paramWatchers.map(function (queryParam) {
    return /*#__PURE__*/React__default.createElement(ParamWatcher, _extends({
      key: queryParam.key
    }, {
      replace: replace,
      delay: delay,
      queryParam: queryParam
    }));
  }));
};

export default URLStateSync;
//# sourceMappingURL=index.esm.js.map
